import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, Gamepad2, Sparkles } from "lucide-react";
import { formatRelativeTime, getDisplayName, getTeamColor } from "./teamIdentity";
import { MemberStack, SessionTypeChip, TeamEmblem, WinRateRing } from "./TeamUI";

/**
 * Summary card for one team. The whole card links to the team page
 * (stretched link); the "name your squad" button sits above that link.
 */
const TeamCard = ({ team, currentUserId, onCustomize }) => {
  const { t, i18n } = useTranslation();
  const { stats } = team;
  const color = getTeamColor(team);
  const teammates = team.members.filter((member) => String(member.id) !== String(currentUserId));
  const lastPlayed = formatRelativeTime(stats.lastPlayedAt, i18n.language);

  return (
    <article
      className="group relative flex flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 transition motion-safe:hover:-translate-y-0.5 hover:shadow-lg focus-within:ring-2 focus-within:ring-orange-400"
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: color }} aria-hidden="true" />
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <TeamEmblem team={team} size="md" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold leading-tight text-[#120239]">
              <Link
                to={`/teams/${team.id}`}
                className="break-words outline-none after:absolute after:inset-0 after:content-['']"
              >
                {team.teamName}
              </Link>
            </h3>
            <p className="mt-1 truncate text-sm text-gray-500">
              {team.isSolo
                ? t("soloTeamSubtitle", { defaultValue: "Your solo identity" })
                : t("withTeammates", {
                    defaultValue: "With {{names}}",
                    names: teammates.map(getDisplayName).join(", "),
                  })}
            </p>
          </div>
          <WinRateRing value={stats.winRate} size={52} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!team.isSolo && <MemberStack members={team.members} max={4} />}
          {Object.entries(stats.sessionTypes)
            .filter(([, count]) => count > 0)
            .map(([type, count]) => (
              <SessionTypeChip key={type} type={type} count={count} />
            ))}
          {stats.currentWinStreak >= 2 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-bold text-orange-700">
              <Flame size={12} aria-hidden="true" />
              {t("winStreakCount", { defaultValue: "{{count}} win streak", count: stats.currentWinStreak })}
            </span>
          )}
        </div>

        <dl className="mt-auto grid grid-cols-3 gap-2 rounded-2xl bg-gray-50 p-3 text-center">
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t("games", { defaultValue: "Games" })}
            </dt>
            <dd className="text-lg font-bold tabular-nums text-gray-900">{stats.gamesPlayed}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t("record", { defaultValue: "W-L" })}
            </dt>
            <dd className="text-lg font-bold tabular-nums text-gray-900">
              {stats.wins}-{stats.losses}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t("lastPlayed", { defaultValue: "Last played" })}
            </dt>
            <dd className="truncate text-sm font-semibold leading-7 text-gray-900">{lastPlayed || "-"}</dd>
          </div>
        </dl>

        {team.isAutoName && onCustomize && (
          <button
            type="button"
            onClick={() => onCustomize(team)}
            className="relative z-10 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#FF9200] to-amber-400 px-4 text-sm font-bold text-[#120239] shadow-md transition active:scale-[0.98] motion-safe:animate-[wiggle_1s_ease-in-out_1]"
          >
            <Sparkles size={16} aria-hidden="true" />
            {team.isSolo
              ? t("nameYourTag", { defaultValue: "Pick your player tag" })
              : t("nameYourSquad", { defaultValue: "Name your squad!" })}
          </button>
        )}
        {stats.gamesPlayed === 0 && (
          <p className="inline-flex items-center gap-2 text-xs text-gray-500">
            <Gamepad2 size={14} aria-hidden="true" />
            {t("noGamesYet", { defaultValue: "No games yet" })}
          </p>
        )}
      </div>
    </article>
  );
};

export default TeamCard;
